import { Check, EyeOff, LockKeyhole, Moon, ShieldCheck, Sparkles } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useId, useRef, useState, type MouseEvent } from "react";
import { ROLE_DESCRIPTIONS, ROLE_LABELS, type PrivatePlayer, type PublicPlayer, type Role } from "@ma-soi/game-core";
import { useVisualEffects } from "../../hooks/VisualEffectsContext";
import { errorMessage } from "../../lib";
import { GameButton } from "../GameUi";
import { SeatAvatar } from "../SeatAvatar";
import { ClawMark } from "./PhaseArt";

const ART_ROOT = "/assets/ui/gothic-v2/";
const ROLE_ART: Record<Role, string> = {
  WEREWOLF: "role-werewolf.webp", VILLAGER: "role-villager.webp", SEER: "role-seer.webp",
  WITCH: "role-witch.webp", HUNTER: "role-hunter.webp", GUARD: "role-guard.webp",
};

export function roleAbility(player: PrivatePlayer, guardRepeatTarget: boolean) {
  switch (player.role) {
    case "WEREWOLF": return { title: "Thức cùng bầy Sói", detail: "Cùng bầy chọn một mục tiêu mỗi đêm." };
    case "VILLAGER": return { title: "Quan sát · Tranh luận · Bỏ phiếu", detail: "Bạn không có hành động ban đêm. Hãy tìm ra Ma sói cùng dân làng." };
    case "SEER": return { title: "Soi một người khác mỗi đêm", detail: "Kết quả riêng cho bạn: Sói / Không phải Sói." };
    case "WITCH": return { title: `${player.healAvailable ? "1" : "0"} bình cứu · ${player.poisonAvailable ? "1" : "0"} bình độc`, detail: "Mỗi bình dùng một lần. Tối đa một bình mỗi đêm." };
    case "HUNTER": return { title: "Phát súng cuối cùng", detail: "Khi đến lượt Thợ săn, chọn một người hoặc bỏ lượt." };
    case "GUARD": return { title: "Che chở một người mỗi đêm", detail: guardRepeatTarget ? "Được bảo vệ cùng một người nhiều đêm liên tiếp." : "Không được bảo vệ cùng một người hai đêm liên tiếp." };
  }
}

function CardArtwork({ src }: { src: string }) {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  return <>
    {state !== "ready" && <div className="reveal-art-fallback"><Moon /><span>{state === "error" ? "Tranh chưa tải được" : ""}</span></div>}
    {state !== "error" && <img src={src} alt="" width="1024" height="1536" draggable={false} decoding="async" onLoad={() => setState("ready")} onError={() => setState("error")} />}
  </>;
}

export function RoleReveal({ privatePlayer, currentPlayer, players, guardRepeatTarget, busy, connected, onAcknowledge }: {
  privatePlayer: PrivatePlayer | null;
  currentPlayer: PublicPlayer;
  players: PublicPlayer[];
  guardRepeatTarget: boolean;
  busy: boolean;
  connected: boolean;
  onAcknowledge: () => Promise<unknown>;
}) {
  const { reduced } = useVisualEffects();
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const focusAfterOpen = useRef(false);
  const [opened, setOpened] = useState(false);
  const [animateFlip, setAnimateFlip] = useState(false);
  const [wolfAwake, setWolfAwake] = useState(false);
  const [confirmed, setConfirmed] = useState(Boolean(privatePlayer?.roleAcknowledged));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const acknowledged = confirmed || Boolean(privatePlayer?.roleAcknowledged);
  const revealed = Boolean(privatePlayer) && (opened || acknowledged);

  useEffect(() => {
    if (!wolfAwake) return;
    if (reduced) { setWolfAwake(false); return; }
    const timer = window.setTimeout(() => setWolfAwake(false), 580);
    const hide = () => { if (document.hidden) setWolfAwake(false); };
    document.addEventListener("visibilitychange", hide);
    return () => { window.clearTimeout(timer); document.removeEventListener("visibilitychange", hide); };
  }, [wolfAwake, reduced]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    // Chốt một chiều theo xác nhận server, không úp lại vì snapshot đến sau.
    if (privatePlayer?.roleAcknowledged) setConfirmed(true);
  }, [privatePlayer?.roleAcknowledged]);

  useEffect(() => {
    if (revealed && focusAfterOpen.current) {
      focusAfterOpen.current = false;
      titleRef.current?.focus({ preventScroll: true });
    }
  }, [revealed]);

  function reveal(event: MouseEvent<HTMLButtonElement>) {
    if (!privatePlayer || revealed) return;
    // Chỉ state trình bày: không gửi xác nhận và không chờ animation/tải ảnh.
    focusAfterOpen.current = true;
    setAnimateFlip(!reduced && event.detail !== 0);
    // Cao trào chỉ do người chơi tự mở lá Sói; snapshot, bàn phím và reconnect không kích hoạt.
    setWolfAwake(privatePlayer.role === "WEREWOLF" && !reduced && event.detail !== 0 && !document.hidden);
    setOpened(true);
  }

  async function acknowledge() {
    if (!revealed || acknowledged || pending || busy || !connected || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      await onAcknowledge();
      if (mounted.current) setConfirmed(true);
    } catch (reason) {
      if (mounted.current) setError(errorMessage(reason));
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  }

  const ability = privatePlayer ? roleAbility(privatePlayer, guardRepeatTarget) : null;
  const pack = privatePlayer?.wolfPack.filter((wolf) => wolf.uid !== currentPlayer.uid) ?? [];

  return (
    <section className="role-reveal" aria-labelledby={titleId} data-revealed={revealed} data-confirmed={acknowledged}>
      <div className="reveal-privacy"><EyeOff aria-hidden="true" /><span>Chỉ bạn nhìn thấy lá vai này</span></div>
      <div className="reveal-stage">
        <div className="reveal-art-column" aria-hidden="true">
          <div className={`role-relic ${revealed ? "is-open" : "is-sealed"} ${wolfAwake && !reduced ? "is-wolf-awake" : ""}`}>
            <motion.div key={reduced ? "still" : "motion"} className="role-relic-flipper" initial={false}
              animate={{ transform: revealed ? "rotateY(180deg)" : "rotateY(0deg)" }}
              transition={{ duration: animateFlip && !reduced ? .5 : 0, ease: [.65, 0, .35, 1] }}
              onAnimationComplete={() => setAnimateFlip(false)}>
              <div className="reveal-card-face reveal-card-back">
                <CardArtwork src={`${ART_ROOT}card-back.webp`} />
                {!revealed && <div className="reveal-card-atmosphere"><span className="reveal-moonlight" /><span className="reveal-mist" /><span className="reveal-glint" /><span className="reveal-embers"><i /><i /><i /></span></div>}
              </div>
              {revealed && privatePlayer && <div className="reveal-card-face reveal-card-front">
                <CardArtwork key={privatePlayer.role} src={`${ART_ROOT}${ROLE_ART[privatePlayer.role]}`} />
                {wolfAwake && !reduced && <span className="wolf-reveal-burst" aria-hidden="true"><ClawMark /></span>}
              </div>}
            </motion.div>
          </div>
          <p className="reveal-art-caption">{revealed ? "Bản mệnh đã định" : "Phong ấn chưa mở"}</p>
        </div>

        <div className="reveal-copy">
          {revealed && privatePlayer && ability ? <>
            <p className="reveal-eyebrow">Vai của bạn</p>
            <h2 id={titleId} ref={titleRef} tabIndex={-1}>{ROLE_LABELS[privatePlayer.role]}</h2>
            <p className="reveal-description">{ROLE_DESCRIPTIONS[privatePlayer.role]}</p>
            <div className="reveal-ability gothic-frame">
              <p><Sparkles aria-hidden="true" />Năng lực của bạn</p>
              <strong>{ability.title}</strong>
              <span>{ability.detail}</span>
            </div>
            {privatePlayer.role === "WEREWOLF" && <section className="reveal-pack" aria-label="Bầy Sói của bạn">
              <h3>Bầy Sói <span>{pack.length} đồng đội</span></h3>
              {pack.length ? <ul>{pack.map((wolf) => {
                const player = players.find((candidate) => candidate.uid === wolf.uid);
                return <li key={wolf.uid}><SeatAvatar seat={player?.seat ?? 99} avatarId={player?.avatarId} name={wolf.name} /><div><strong>{wolf.name}</strong><span>{player ? `Ghế ${String(player.seat).padStart(2, "0")}` : "Đồng đội của bạn"}</span></div></li>;
              })}</ul> : <p>Bạn là Ma sói duy nhất trong ván này.</p>}
            </section>}
          </> : <>
            <p className="reveal-eyebrow">Nghi thức nhận vai</p>
            <h2 id={titleId}>Một lá bài.{" "}<br />Một số phận.</h2>
            <p className="reveal-description">Vai của bạn nằm sau phong ấn. Mở lá bài, đọc năng lực và giữ bí mật với dân làng.</p>
            <p className="reveal-sealed-note"><LockKeyhole aria-hidden="true" />Micro tạm khóa trong lúc nhận vai.</p>
          </>}

          <div className="reveal-actions">
            {error && <p className="reveal-error" role="alert">{error}</p>}
            {!revealed ? <>
              <GameButton variant="gold" fullWidth icon={<Sparkles aria-hidden="true" />} onClick={reveal} disabled={!privatePlayer}>
                {privatePlayer ? "Mở lá bài định mệnh" : "Đang nhận lá bài…"}
              </GameButton>
              <p>{privatePlayer ? "Sau khi đọc vai, hãy xác nhận đã ghi nhớ." : "Đang đồng bộ vai riêng của bạn."}</p>
            </> : <>
              <GameButton variant="gold" fullWidth icon={acknowledged ? <Check aria-hidden="true" /> : <ShieldCheck aria-hidden="true" />} onClick={() => void acknowledge()} disabled={acknowledged || pending || busy || !connected}>
                {pending && !acknowledged ? "Đang xác nhận…" : "Đã ghi nhớ"}
              </GameButton>
              <p role="status">{acknowledged ? "Đã xác nhận · Chờ dân làng còn lại." : !connected ? "Đang kết nối lại · Bạn vẫn có thể đọc vai." : "Ghi nhớ vai của bạn trước khi đêm bắt đầu."}</p>
            </>}
          </div>
        </div>
      </div>
    </section>
  );
}
